import type { MouseEvent } from 'react';

declare global {
	interface Window {
		HubSpotConversations?: {
			widget: {
				load: (options?: { widgetOpen?: boolean }) => void;
				open: () => void;
				close: () => void;
				remove: () => void;
				status: () => { loaded: boolean; pending?: boolean };
			};
			on: (event: string, callback: () => void) => void;
			off: (event: string, callback: () => void) => void;
		};
		hsConversationsSettings?: { loadImmediately?: boolean };
		hsConversationsOnReady?: (() => void)[];
	}
}

/** The HubSpot portal whose chat Guest Support answers; unset means no chat. */
export const hubSpotPortalId = () =>
	process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID?.trim() || null;

/** How long to wait for HubSpot's script before falling back to the link. */
const READY_TIMEOUT_MS = 8000;

/* Hidden by globals.css until asked for, as in portal-next: no launcher bubble. */
const CONTAINER_ID = 'hubspot-messages-iframe-container';
const showContainer = (visible: boolean) => {
	const container = document.getElementById(CONTAINER_ID);
	container?.style.setProperty(
		'display',
		visible ? 'initial' : 'none',
		'important',
	);
	container?.style.setProperty(
		'visibility',
		visible ? 'visible' : 'hidden',
		'important',
	);
};

let loading: Promise<boolean> | undefined;

/**
 * Loads HubSpot's script the first time the chat is asked for, so pages
 * that never open it never load it. Resolves false if it does not arrive.
 */
const loadHubSpot = (portalId: string) => {
	if (window.HubSpotConversations) return Promise.resolve(true);
	loading ??= new Promise<boolean>((resolve) => {
		const fail = () => {
			loading = undefined;
			resolve(false);
		};
		const timer = window.setTimeout(fail, READY_TIMEOUT_MS);
		window.hsConversationsSettings = {
			...window.hsConversationsSettings,
			loadImmediately: false,
		};
		window.hsConversationsOnReady = [
			...(window.hsConversationsOnReady ?? []),
			() => {
				window.clearTimeout(timer);
				// The container only exists once the widget has loaded.
				window.HubSpotConversations?.on('widgetLoaded', () =>
					showContainer(true),
				);
				window.HubSpotConversations?.on('widgetClosed', () =>
					showContainer(false),
				);
				resolve(true);
			},
		];
		const script = document.createElement('script');
		script.id = 'hs-script-loader';
		script.src = `https://js.hs-scripts.com/${portalId}.js`;
		script.async = true;
		script.onerror = () => {
			window.clearTimeout(timer);
			fail();
		};
		document.body.appendChild(script);
	});
	return loading;
};

/**
 * Opens HubSpot's chat. Returns false when the portal is not configured or
 * the script is blocked, so the caller can fall back to a link.
 */
export const openHubSpotChat = async (): Promise<boolean> => {
	const portalId = hubSpotPortalId();
	if (!portalId || !(await loadHubSpot(portalId))) return false;
	const widget = window.HubSpotConversations?.widget;
	if (!widget) return false;
	showContainer(true);
	if (widget.status().loaded) widget.open();
	// No chatflow matched this page yet: load it, opened.
	else widget.load({ widgetOpen: true });
	return true;
};

/**
 * For a help or live chat link: opens HubSpot's chat in place of following
 * the link, and opens it in a new tab after all if the chat cannot open.
 */
export const openChatInsteadOfLink = (event: MouseEvent<HTMLAnchorElement>) => {
	if (!hubSpotPortalId()) return;
	event.preventDefault();
	const { href } = event.currentTarget;
	void openHubSpotChat().then((opened) => {
		// The click's user activation may have lapsed: a blocked tab follows the link here.
		if (!opened && !window.open(href, '_blank', 'noopener'))
			window.location.assign(href);
	});
};
